import React, { useState, useEffect } from "react";
import "../styles/userProfile.css";
import { useAuth } from "../context/AuthContext";
import {
  saveUserSettings,
  getUserSettings,
  getUserByReferralCode,
  rewardReferral,
} from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineLeft,
  AiOutlineCamera,
} from "react-icons/ai";
import { FaUserCircle } from "react-icons/fa";
import { auth } from "../firebase";
import { updateProfile } from "firebase/auth";

// ============================================================
// DELIVERY AREAS
// ============================================================

const ALLOWED_AREAS = [
  "Alathur / Alathur Gate",
  "Anangur / Anangur Pirivu",
  "Anangur Village & Pudur",
  "Athanur Amman Kovil",
  "Avarangadu",
  "C.N. Palayam (Chinnappampalayam)",
  "Cauvery R.S. (Railway Station Road)",
  "Chinnagoundanur",
  "Devanankurichi",
  "Elandakuttai",
  "Emapalli / Vittampalayam Pirivu",
  "Kailasampalayam",
  "Kallangattuvalasu",
  "Kallikkadu",
  "Kaveripatti Pirivu",
  "Kokkarayanpettai (Cauvery riverside belt)",
  "Komarapalayam Town (Salem Main Road, Bus Stand, New Pallipalayam Road, NH-544 Bypass Bridge, Cauvery River Bridge border)",
  "Kootapalli / Kootapalli Colony",
  "Kumaramangalam / Kumaramangalam Tollgate",
  "Kuppandapalayam / Chinna Kuppandapalayam",
  "Modamangalam",
  "Morur / Morur Pirivu",
  "Paalmadai",
  "Pachampalayam",
  "Padaiveedu / Sundaram Colony",
  "Padaiveedu Town Panchayat",
  "Pallipalayam Agraharam",
  "Pallipalayam Town (Bus Stand, Railway Gate, Odapalli, Cauvery Bridge Junction)",
  "Pappampalayam",
  "Pirivu Road",
  "Sankagiri R.S. (Railway Colony, Pullipalayam, Cement Factory Area)",
  "Sankagiri Town (Old Bus Stand, New Bye-pass, Sankagiri Durg Fort foothills, Court & Taluk Office junction)",
  "Sankari West (ICL Colony & Quarry Area)",
  "SPB Colony / Seshasayee Paper Board area",
  "Thokavadi (KSR Institutions Hub / Lion City)",
  "Tiruchengode Town (Arthanareeswarar Foothill, Gandhi Statue, Sankagiri Road, Velur Road, Old & New Bus Stands)",
  "Valayakaranur",
  "Varagurampatti",
  "Vellakkalpatti / Sanyasikaradu",
  "Veppadai (Bus Stop, Bazaar, Powerloom Industrial Belt)",
  "Veppadai Bypass",
  "Vittampalayam",
];

// ============================================================
// GENERATE REFERRAL CODE
// ============================================================

const generateReferralCode = (uid) => {
  return "EYAL" + uid.substring(0, 6).toUpperCase();
};

// ============================================================
// COMPRESS PROFILE IMAGE
//
// Firestore has a ~1 MiB document field limit.
// Raw Base64 photos can easily exceed that.
//
// This function:
// 1. Loads the image
// 2. Resizes it
// 3. Converts it to JPEG
// 4. Compresses it
// 5. Returns a much smaller Base64 data URL
// ============================================================

const compressProfileImage = (
  file,
  maxWidth = 500,
  maxHeight = 500,
  quality = 0.70
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const image = new Image();

      image.onload = () => {
        try {
          let width = image.width;
          let height = image.height;

          // ----------------------------------------------------
          // Resize while keeping aspect ratio
          // ----------------------------------------------------

          if (
            width > maxWidth ||
            height > maxHeight
          ) {
            const widthRatio =
              maxWidth / width;

            const heightRatio =
              maxHeight / height;

            const ratio =
              Math.min(
                widthRatio,
                heightRatio
              );

            width =
              Math.round(
                width * ratio
              );

            height =
              Math.round(
                height * ratio
              );
          }

          // ----------------------------------------------------
          // Canvas
          // ----------------------------------------------------

          const canvas =
            document.createElement(
              "canvas"
            );

          canvas.width = width;
          canvas.height = height;

          const ctx =
            canvas.getContext(
              "2d"
            );

          if (!ctx) {
            reject(
              new Error(
                "Could not create canvas context"
              )
            );
            return;
          }

          // ----------------------------------------------------
          // White background
          //
          // Useful if original image is PNG with transparency.
          // ----------------------------------------------------

          ctx.fillStyle = "#ffffff";

          ctx.fillRect(
            0,
            0,
            width,
            height
          );

          // ----------------------------------------------------
          // Draw resized image
          // ----------------------------------------------------

          ctx.drawImage(
            image,
            0,
            0,
            width,
            height
          );

          // ----------------------------------------------------
          // Convert to compressed JPEG
          // ----------------------------------------------------

          const compressed =
            canvas.toDataURL(
              "image/jpeg",
              quality
            );

          if (
            !compressed ||
            compressed.length < 100
          ) {
            reject(
              new Error(
                "Image compression failed"
              )
            );
            return;
          }

          // ----------------------------------------------------
          // Safety check
          //
          // Keep well below Firestore's ~1 MiB field limit.
          // ----------------------------------------------------

          const approxBytes =
            Math.ceil(
              compressed.length * 0.75
            );

          console.log(
            "Compressed profile photo:",
            Math.round(
              approxBytes / 1024
            ),
            "KB"
          );

          if (
            approxBytes >
            800 * 1024
          ) {
            reject(
              new Error(
                "Compressed image is still too large"
              )
            );
            return;
          }

          resolve(
            compressed
          );
        } catch (error) {
          reject(error);
        }
      };

      image.onerror = () => {
        reject(
          new Error(
            "Unable to load selected image"
          )
        );
      };

      image.src =
        reader.result;
    };

    reader.onerror = () => {
      reject(
        new Error(
          "Unable to read selected image"
        )
      );
    };

    reader.readAsDataURL(file);
  });
};

// ============================================================
// USER SETTINGS
// ============================================================

export default function UserSettings() {
  const {
    user,
    refreshUserProfile,
  } = useAuth();

  const navigate =
    useNavigate();

  // ============================================================
  // FORM STATE
  // ============================================================

  const [form, setForm] =
    useState({
      name: "",
      mobile: "",
      address: "",
      area: "",
    });

  // ============================================================
  // PROFILE PHOTO
  // ============================================================

  const [
    profilePhoto,
    setProfilePhoto,
  ] = useState(null);

  // ============================================================
  // LOADING
  // ============================================================

  const [loading, setLoading] =
    useState(true);

  // ============================================================
  // PHOTO UPLOADING
  // ============================================================

  const [
    photoUploading,
    setPhotoUploading,
  ] = useState(false);

  // ============================================================
  // AREA DROPDOWN
  // ============================================================

  const [
    areaDropdownOpen,
    setAreaDropdownOpen,
  ] = useState(false);

  // ============================================================
  // LOAD USER DATA
  // ============================================================

  useEffect(() => {
    if (!user?.uid) return;

    let cancelled = false;

    const loadUserData = async () => {
      try {
        const data =
          await getUserSettings(
            user.uid
          );

        if (cancelled) return;

        // ------------------------------------------------------
        // Firebase Auth phone number
        // ------------------------------------------------------

        const authMobile =
          user.phoneNumber ||
          "";

        // ------------------------------------------------------
        // Firestore phone number
        // ------------------------------------------------------

        const firestoreMobile =
          data?.mobile ||
          "";

        // ------------------------------------------------------
        // Prefer Firestore.
        // If missing, use Firebase Auth.
        // ------------------------------------------------------

        const mobileSource =
          firestoreMobile ||
          authMobile ||
          "";

        // ------------------------------------------------------
        // Convert +91XXXXXXXXXX
        // into XXXXXXXXXX
        // ------------------------------------------------------

        const displayMobile =
          mobileSource
            .replace(
              /^\+91/,
              ""
            )
            .replace(
              /\D/g,
              ""
            )
            .slice(-10);

        // ------------------------------------------------------
        // If Firestore doesn't have mobile,
        // save Firebase Auth phone number.
        // ------------------------------------------------------

        if (
          authMobile &&
          !firestoreMobile
        ) {
          try {
            await saveUserSettings(
              user.uid,
              {
                mobile:
                  authMobile,
              }
            );

            console.log(
              "✅ Firebase Auth phone number saved to Firestore"
            );
          } catch (saveError) {
            console.error(
              "⚠️ Could not save Auth phone number:",
              saveError
            );
          }
        }

        // ------------------------------------------------------
        // Existing Firestore profile
        // ------------------------------------------------------

        if (data) {
          setForm({
            name:
              data.name ||
              user.displayName ||
              "",

            mobile:
              displayMobile,

            address:
              data.address ||
              "",

            area:
              ALLOWED_AREAS.includes(
                data.area
              )
                ? data.area
                : "",
          });

          setProfilePhoto(
            data.photoURL ||
              user.photoURL ||
              null
          );
        }

        // ------------------------------------------------------
        // No Firestore profile
        // ------------------------------------------------------

        else {
          setForm({
            name:
              user.displayName ||
              "",

            mobile:
              displayMobile,

            address:
              "",

            area:
              "",
          });

          setProfilePhoto(
            user.photoURL ||
              null
          );

          // ----------------------------------------------------
          // Create basic user record
          // ----------------------------------------------------

          const newUserData = {
            name:
              user.displayName ||
              "",

            mobile:
              authMobile,

            address:
              "",

            area:
              "",

            photoURL:
              user.photoURL ||
              "",

            referralCode:
              generateReferralCode(
                user.uid
              ),

            referredByUid:
              "",

            referralRewarded:
              false,

            referralCount:
              0,
          };

          try {
            await saveUserSettings(
              user.uid,
              newUserData
            );

            console.log(
              "✅ New user profile created"
            );
          } catch (saveError) {
            console.error(
              "⚠️ Failed to create user profile:",
              saveError
            );
          }
        }
      } catch (err) {
        console.error(
          "Error fetching user settings:",
          err
        );

        // ------------------------------------------------------
        // Fallback to Firebase Auth
        // ------------------------------------------------------

        const authMobile =
          user.phoneNumber ||
          "";

        const displayMobile =
          authMobile
            .replace(
              /^\+91/,
              ""
            )
            .replace(
              /\D/g,
              ""
            )
            .slice(-10);

        setForm({
          name:
            user.displayName ||
            "",

          mobile:
            displayMobile,

          address:
            "",

          area:
            "",
        });

        setProfilePhoto(
          user.photoURL ||
            null
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUserData();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // ============================================================
  // PROFILE PICTURE UPLOAD
  // ============================================================

  const handleProfilePicUpload = async (
    e
  ) => {
    const file =
      e.target.files?.[0];

    if (!file) return;

    // ----------------------------------------------------------
    // Check file type
    // ----------------------------------------------------------

    if (
      !file.type.startsWith(
        "image/"
      )
    ) {
      alert(
        "Please select an image file."
      );

      e.target.value = "";
      return;
    }

    // ----------------------------------------------------------
    // Original file limit
    //
    // Compression happens before Firestore.
    // ----------------------------------------------------------

    if (
      file.size >
      10 * 1024 * 1024
    ) {
      alert(
        "Please choose an image smaller than 10 MB."
      );

      e.target.value = "";
      return;
    }

    if (!user?.uid) {
      alert(
        "User not logged in."
      );

      e.target.value = "";
      return;
    }

    setPhotoUploading(true);

    try {
      // --------------------------------------------------------
      // COMPRESS IMAGE
      // --------------------------------------------------------

      const compressedPhoto =
        await compressProfileImage(
          file,
          500,
          500,
          0.70
        );

      console.log(
        "Photo ready for Firestore"
      );

      // --------------------------------------------------------
      // SAVE ONLY COMPRESSED IMAGE
      // --------------------------------------------------------

      await saveUserSettings(
        user.uid,
        {
          photoURL:
            compressedPhoto,
        }
      );

      // --------------------------------------------------------
      // UPDATE SCREEN IMMEDIATELY
      // --------------------------------------------------------

      setProfilePhoto(
        compressedPhoto
      );

      alert(
        "✅ Profile picture updated successfully!"
      );
    } catch (error) {
      console.error(
        "❌ Error updating profile picture:",
        error
      );

      alert(
        "Failed to update profile picture. Please choose another image."
      );
    } finally {
      setPhotoUploading(false);

      // --------------------------------------------------------
      // Allow selecting same image again
      // --------------------------------------------------------

      e.target.value = "";
    }
  };

  // ============================================================
  // SAVE PROFILE
  // ============================================================

  const handleSave = async () => {
    const {
      name,
      mobile,
      address,
      area,
    } = form;

    // ==========================================================
    // NAME VALIDATION
    // ==========================================================

    if (!name.trim()) {
      alert(
        "Please enter your name."
      );
      return;
    }

    // ==========================================================
    // MOBILE VALIDATION
    // ==========================================================

    if (
      mobile &&
      !/^[6-9]\d{9}$/.test(
        mobile
      )
    ) {
      alert(
        "Enter a valid 10-digit mobile number."
      );
      return;
    }

    // ==========================================================
    // AREA VALIDATION
    // ==========================================================

    if (!area) {
      alert(
        "Please select your delivery area."
      );
      return;
    }

    if (
      !ALLOWED_AREAS.includes(
        area
      )
    ) {
      alert(
        "Please select a valid delivery area."
      );
      return;
    }

    // ==========================================================
    // MOBILE
    //
    // Prefer form value.
    // If empty, use Firebase Auth phone.
    // ==========================================================

    const cleanMobile =
      (mobile || "")
        .replace(
          /\D/g,
          ""
        );

    const authMobile =
      (user?.phoneNumber || "")
        .replace(
          /\D/g,
          ""
        );

    let finalMobile = "";

    if (
      cleanMobile.length === 10
    ) {
      finalMobile =
        `+91${cleanMobile}`;
    } else if (
      authMobile.length === 12 &&
      authMobile.startsWith(
        "91"
      )
    ) {
      finalMobile =
        `+${authMobile}`;
    }

    try {
      // ========================================================
      // EXISTING USER DATA
      // ========================================================

      const existingData =
        await getUserSettings(
          user.uid
        );

      // ========================================================
      // REFERRAL
      // ========================================================

      let referredByUid =
        existingData?.referredByUid ||
        "";

      const referralCode =
        localStorage.getItem(
          "eyalReferral"
        );

      if (
        referralCode &&
        !referredByUid &&
        !existingData?.referralRewarded
      ) {
        const referrer =
          await getUserByReferralCode(
            referralCode
          );

        if (
          referrer &&
          referrer.uid !==
            user.uid
        ) {
          referredByUid =
            referrer.uid;
        }
      }

      // ========================================================
      // PHOTO
      //
      // Keep the already stored compressed photo.
      // Never replace it with a huge Auth photo URL.
      // ========================================================

      const finalPhoto =
        profilePhoto ||
        existingData?.photoURL ||
        "";

      // ========================================================
      // SAVE TO FIRESTORE
      // ========================================================

      await saveUserSettings(
        user.uid,
        {
          name:
            name.trim(),

          mobile:
            finalMobile ||
            existingData?.mobile ||
            user?.phoneNumber ||
            "",

          address:
            address.trim(),

          area,

          photoURL:
            finalPhoto,

          referralCode:
            existingData?.referralCode ||
            generateReferralCode(
              user.uid
            ),

          referredByUid,

          referralRewarded:
            existingData?.referralRewarded ??
            false,

          referralCount:
            existingData?.referralCount ??
            0,
        }
      );

      // ========================================================
      // REWARD REFERRAL
      // ========================================================

      if (
        referredByUid &&
        !existingData?.referralRewarded
      ) {
        try {
          await rewardReferral(
            user.uid
          );

          localStorage.removeItem(
            "eyalReferral"
          );
        } catch (err) {
          console.error(
            "Referral reward failed:",
            err
          );
        }
      }

      // ========================================================
      // UPDATE FIREBASE AUTH PROFILE
      //
      // IMPORTANT:
      // Only update displayName here.
      //
      // Do NOT put Base64 photoURL into Firebase Auth.
      // ========================================================

      const currentUser =
        auth.currentUser;

      if (
        currentUser
      ) {
        await updateProfile(
          currentUser,
          {
            displayName:
              name.trim(),
          }
        );
      }

      // ========================================================
      // SYNC GLOBAL AUTH STATE
      // ========================================================

      if (
        refreshUserProfile
      ) {
        await refreshUserProfile({
          displayName:
            name.trim(),
        });
      }

      // ========================================================
      // SUCCESS
      // ========================================================

      alert(
        "✅ Profile updated successfully!"
      );

      window.location.href =
        "/home";
    } catch (err) {
      console.error(
        "Error saving settings:",
        err
      );

      alert(
        "❌ Failed to update profile. Please try again."
      );
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div
        className="profile-page"
        style={{
          textAlign:
            "center",
          padding:
            "60px 20px",
        }}
      >
        <h3>
          Loading your details...
        </h3>
      </div>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="profile-page">

      {/* ======================================================
          BACK
      ====================================================== */}

      <div
        style={{
          display:
            "flex",
          alignItems:
            "center",
          marginBottom:
            "10px",
        }}
      >
        <button
          className="back-btn"
          onClick={() =>
            navigate(-1)
          }
        >
          <AiOutlineLeft
            style={{
              marginRight:
                "6px",
            }}
          />

          Back
        </button>
      </div>

      {/* ======================================================
          TITLE
      ====================================================== */}

      <h2
        style={{
          fontWeight:
            "700",
          fontSize:
            "22px",
          marginBottom:
            "20px",
        }}
      >
        Edit Profile
      </h2>

      {/* ======================================================
          FORM CARD
      ====================================================== */}

      <div
        style={{
          background:
            "#fff",
          borderRadius:
            "16px",
          padding:
            "20px 16px",
          boxShadow:
            "0 4px 10px rgba(0,0,0,0.05)",
          textAlign:
            "left",
          width:
            "100%",
          maxWidth:
            "100%",
          overflow:
            "visible",
        }}
      >

        {/* ====================================================
            PROFILE PHOTO
        ==================================================== */}

        <div
          className="profile-header"
          style={{
            marginBottom:
              "25px",
          }}
        >
          <div className="pf-avatar-box">

            {profilePhoto ? (
              <img
                src={
                  profilePhoto
                }
                alt="Profile"
                className="pf-avatar"
                onError={(
                  e
                ) => {
                  e.currentTarget.style.display =
                    "none";
                }}
              />
            ) : (
              <FaUserCircle
                className="pf-avatar-icon"
              />
            )}

            {/* CAMERA */}

            <label
              htmlFor="profilePic"
              className="camera-icon"
              style={{
                cursor:
                  photoUploading
                    ? "wait"
                    : "pointer",
                opacity:
                  photoUploading
                    ? 0.5
                    : 1,
              }}
            >
              <AiOutlineCamera />
            </label>

            {/* FILE INPUT */}

            <input
              type="file"
              id="profilePic"
              accept="image/*"
              style={{
                display:
                  "none",
              }}
              disabled={
                photoUploading
              }
              onChange={
                handleProfilePicUpload
              }
            />

          </div>

          <h3>
            {form.name ||
              "User"}
          </h3>

          {user?.email && (
            <p>
              {user.email}
            </p>
          )}

          {photoUploading && (
            <small
              style={{
                display:
                  "block",
                marginTop:
                  "6px",
                color:
                  "#64748b",
              }}
            >
              Compressing and uploading photo...
            </small>
          )}
        </div>

        {/* ====================================================
            NAME
        ==================================================== */}

        <label>
          Name
        </label>

        <input
          type="text"
          className="input-box"
          placeholder="Enter your name"
          value={
            form.name
          }
          onChange={(
            e
          ) =>
            setForm({
              ...form,
              name:
                e.target.value,
            })
          }
        />

        {/* ====================================================
            MOBILE
        ==================================================== */}

        <label>
          Mobile Number
        </label>

        <div
          style={{
            display:
              "flex",
            alignItems:
              "center",
            width:
              "100%",
            maxWidth:
              "100%",
          }}
        >
          <span
            style={{
              fontWeight:
                "600",
              marginRight:
                "8px",
              whiteSpace:
                "nowrap",
            }}
          >
            +91
          </span>

          <input
            type="tel"
            maxLength="10"
            className="input-box"
            style={{
              width:
                "100%",
              flex:
                "1",
              minWidth:
                0,
              backgroundColor:
                "#f1f5f9",
              color:
                "#475569",
              cursor:
                "not-allowed",
            }}
            value={
              form.mobile
            }
            readOnly
          />
        </div>

        {/* ====================================================
            ADDRESS
        ==================================================== */}

        <label>
          Address
        </label>

        <textarea
          className="input-box"
          rows="2"
          placeholder="Flat / Door No, Street"
          value={
            form.address
          }
          onChange={(
            e
          ) =>
            setForm({
              ...form,
              address:
                e.target.value,
            })
          }
        />

        {/* ====================================================
            DELIVERY AREA
        ==================================================== */}

        <label>
          Select Your Area{" "}
          <span
            style={{
              color:
                "red",
            }}
          >
            *
          </span>
        </label>

        {/* ====================================================
            DROPDOWN
        ==================================================== */}

        <div className="area-dropdown">

          <button
            type="button"
            className={`area-dropdown-button ${
              form.area
                ? "selected"
                : ""
            }`}
            onClick={() =>
              setAreaDropdownOpen(
                (prev) =>
                  !prev
              )
            }
          >
            <span className="area-selected-text">
              {form.area ||
                "Select Delivery Area *"}
            </span>

            <span className="area-dropdown-arrow">
              {areaDropdownOpen
                ? "▲"
                : "▼"}
            </span>
          </button>

          {areaDropdownOpen && (
            <div className="area-dropdown-menu">

              <div
                className="area-dropdown-option placeholder"
                onClick={() => {
                  setForm({
                    ...form,
                    area: "",
                  });

                  setAreaDropdownOpen(
                    false
                  );
                }}
              >
                Select Your Area
              </div>

              {ALLOWED_AREAS.map(
                (
                  area
                ) => (
                  <div
                    key={
                      area
                    }
                    className={`area-dropdown-option ${
                      form.area ===
                      area
                        ? "active"
                        : ""
                    }`}
                    onClick={() => {
                      setForm({
                        ...form,
                        area,
                      });

                      setAreaDropdownOpen(
                        false
                      );
                    }}
                  >
                    {
                      area
                    }
                  </div>
                )
              )}

            </div>
          )}

        </div>

        {/* ====================================================
            SAVE
        ==================================================== */}

        <button
          className="save-btn"
          onClick={
            handleSave
          }
          disabled={
            photoUploading
          }
          style={{
            opacity:
              photoUploading
                ? 0.6
                : 1,
            cursor:
              photoUploading
                ? "not-allowed"
                : "pointer",
          }}
        >
          Save Changes
        </button>

      </div>
    </div>
  );
}